import React, { useState, useEffect } from 'react';
import { 
  Container, 
  Stepper, 
  Step, 
  StepLabel, 
  Box, 
  Typography, 
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Paper,
  Stack,
  Chip,
  alpha
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Receipt as OrderIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import axios from 'axios';
import { useParams } from 'react-router-dom';
import { getPrePaymentStageIndex } from './prePaymentStageMap';
import PrePaymentProcessingPayment from './PrePaymentProcessingPayment';
import PrePaymentPending from './PrePaymentPending';
import PrePaymentProcessing from './PrePaymentProcessing';
import PrePaymentOutForDelivery from './PrePaymentOutForDelivery';
import PrePaymentDelivered from './PrePaymentDelivered';
import PrePaymentFeedback from './PrePaymentFeedback';
import config from '../config';
import { useTranslation } from 'react-i18next';

const API_BASE_URL = `${config.API_BASE_URL}/Orders`;

const PrePaymentStatusPage = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  
  const user = (() => {
    try {
      return JSON.parse(localStorage.getItem('user'));
    } catch {
      return null;
    }
  })();
  
  const userId = user?.Id || user?.id;
  const { orderId } = useParams();
  const [order, setOrder] = useState(null);
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ✅ Fetch order function
  const fetchOrder = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/${orderId}?userId=${userId}`);
      setOrder(res.data);
      setStep(getPrePaymentStageIndex(res.data.Status));
      setLoading(false);
      setError(null);
    } catch (err) {
      setError(t('orderStatus.errorFetch') || 'Failed to fetch order');
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!orderId || !userId) {
      setOrder(null);
      setLoading(false);
      setError(t('orderStatus.errorLogin') || 'Please login to view order');
      return;
    }
    
    let isMounted = true;
    let intervalId = null;
    
    const fetchOrderWrapped = async () => {
      if (isMounted) await fetchOrder();
    };
    
    fetchOrderWrapped();
    intervalId = setInterval(fetchOrderWrapped, 30000);
    
    return () => {
      isMounted = false;
      if (intervalId) clearInterval(intervalId);
    };
    // eslint-disable-next-line
  }, [orderId, userId]);

  // ✅ Step labels
  const stepLabels = [
    t('prePayment.stages.processingPayment', 'Payment Verification'),
    t('prePayment.stages.confirmed', 'Order Confirmed'),
    t('prePayment.stages.processing', 'Preparing Order'),
    t('prePayment.stages.outForDelivery', 'Out for Delivery'),
    t('prePayment.stages.delivered', 'Delivered'),
    t('orderStages.feedback.title', 'Feedback'),
  ];
  
  // ✅ Stage components
  const stages = order ? [
    <PrePaymentProcessingPayment key="0" order={order} stageName={stepLabels[0]} />,
    <PrePaymentPending key="1" order={order} stageName={stepLabels[1]} />,
    <PrePaymentProcessing key="2" order={order} stageName={stepLabels[2]} />,
    <PrePaymentOutForDelivery key="3" order={order} stageName={stepLabels[3]} />,
    <PrePaymentDelivered key="4" order={order} stageName={stepLabels[4]} />,
    <PrePaymentFeedback key="5" order={order} stageName={stepLabels[5]} onSubmitted={fetchOrder} />,
  ] : [];

  // ✅ Mobile-Optimized Loading State
  if (loading) {
    return (
      <Box sx={{ 
        display: 'flex', 
        flexDirection: 'column',
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '80vh',
        gap: 2,
        px: 2
      }}>
        <CircularProgress size={isMobile ? 50 : 60} />
        <Typography 
          variant="body1" 
          color="text.secondary"
          sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}
        >
          {t('orderStatus.loading') || 'Loading order...'}
        </Typography>
      </Box>
    );
  }

  // ✅ Mobile-Optimized Error State
  if (error) {
    return (
      <Container maxWidth="sm" sx={{ mt: { xs: 8, sm: 10 }, px: { xs: 2, sm: 3 } }}>
        <Alert 
          severity="error" 
          icon={<InfoIcon sx={{ fontSize: { xs: 20, sm: 24 } }} />}
          sx={{ 
            borderRadius: { xs: 2, sm: 3 },
            fontSize: { xs: '0.9rem', sm: '1rem' },
            p: { xs: 2, sm: 2.5 }
          }}
        >
          {error}
        </Alert>
      </Container>
    );
  }

  // ✅ No Order ID
  if (!orderId) {
    return (
      <Container maxWidth="sm" sx={{ mt: { xs: 8, sm: 10 }, px: { xs: 2, sm: 3 } }}>
        <Alert severity="warning" sx={{ borderRadius: { xs: 2, sm: 3 } }}>
          {t('orderStatus.errorNoId') || 'No order ID provided'}
        </Alert>
      </Container>
    );
  }

  // ✅ Order Not Found
  if (!order) {
    return (
      <Container maxWidth="sm" sx={{ mt: { xs: 8, sm: 10 }, px: { xs: 2, sm: 3 } }}>
        <Alert severity="info" sx={{ borderRadius: { xs: 2, sm: 3 } }}>
          {t('orderStatus.errorNotFound') || 'Order not found'}
        </Alert>
      </Container>
    );
  }

  return (
    <Container 
      maxWidth="md" 
      sx={{ 
        mt: { xs: 10, sm: 12 }, 
        mb: { xs: 6, sm: 8 },
        px: { xs: 2, sm: 3 }
      }}
    >
      {/* ✅ Order Info Header (Mobile-Optimized) */}
      <Paper
        elevation={2}
        sx={{
          p: { xs: 2, sm: 2.5 },
          mb: { xs: 3, sm: 4 },
          borderRadius: { xs: 2, sm: 3 },
          bgcolor: alpha(theme.palette.success.main, 0.05),
          border: '1px solid',
          borderColor: alpha(theme.palette.success.main, 0.2)
        }}
      >
        <Stack 
          direction={{ xs: 'column', sm: 'row' }} 
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          justifyContent="space-between"
          spacing={{ xs: 1.5, sm: 0 }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <PaymentIcon sx={{ fontSize: { xs: 28, sm: 32 }, color: 'success.main' }} />
            <Box>
              <Typography 
                variant="h6" 
                fontWeight={700}
                sx={{ fontSize: { xs: '1.1rem', sm: '1.25rem' } }}
              >
                {t('prePayment.title') || 'Pre-Payment Order'}
              </Typography>
              <Typography 
                variant="body2" 
                color="text.secondary"
                sx={{ fontSize: { xs: '0.8rem', sm: '0.85rem' } }}
              >
                {t('orderId') || 'Order ID'}: <strong>{orderId}</strong>
              </Typography>
            </Box>
          </Stack>
          <Chip
            label={stepLabels[step]}
            color="success"
            sx={{
              fontSize: { xs: '0.8rem', sm: '0.85rem' },
              height: { xs: 28, sm: 32 },
              fontWeight: 600,
              alignSelf: { xs: 'flex-start', sm: 'center' }
            }}
          />
        </Stack>
      </Paper>

      {/* ✅ Mobile-Optimized Stepper */}
      <Box sx={{ width: '100%', mb: { xs: 4, sm: 6 } }}>
        <Stepper 
          activeStep={step} 
          alternativeLabel={!isMobile}
          orientation={isMobile ? 'vertical' : 'horizontal'}
          sx={{
            '& .MuiStepConnector-line': {
              borderColor: isMobile ? 'divider' : undefined
            }
          }}
        >
          {stepLabels.map((label, index) => (
            <Step key={index}>
              <StepLabel 
                StepIconProps={{
                  sx: { 
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    '&.Mui-active': { 
                      color: theme.palette.success.main,
                      transform: isMobile ? 'none' : 'scale(1.1)'
                    },
                    '&.Mui-completed': { 
                      color: theme.palette.success.main 
                    },
                  }
                }}
              >
                <Typography 
                  sx={{ 
                    fontFamily: 'Cairo, sans-serif', 
                    fontWeight: step === index ? 700 : 600,
                    fontSize: { xs: '0.8rem', sm: '0.85rem' },
                    color: step === index ? 'success.main' : 'text.secondary'
                  }}
                >
                  {label}
                </Typography>
              </StepLabel>
            </Step>
          ))}
        </Stepper>
      </Box>
      
      {/* ✅ Content Area with Mobile-Optimized Background */}
      <Box sx={{
        position: 'relative',
        minHeight: { xs: 300, sm: 400 },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: { xs: '110%', sm: '120%' },
          height: { xs: '110%', sm: '120%' },
          background: 'radial-gradient(circle, rgba(76,175,80,0.05) 0%, rgba(255,255,255,0) 70%)',
          zIndex: -1,
          borderRadius: '50%',
          pointerEvents: 'none'
        }
      }}>
        {stages[step]}
      </Box>
    </Container>
  );
};

export default PrePaymentStatusPage;